import { capRecentArtistsMap, coerceRecentArtistsMap, upsertRecentArtistsUsage } from '../utils/artistRecentUsageUtils'

export const ARTIST_SETTINGS = 'artistSettings'

const MAX_RECENT_ARTISTS_PERSISTED = 50
export const APPOINTMENT_INTERACTION_MODE_NOVICE = 'novice'
export const APPOINTMENT_INTERACTION_MODE_EXPERT = 'expert'
const APPOINTMENT_INTERACTION_MODES = [
    APPOINTMENT_INTERACTION_MODE_NOVICE,
    APPOINTMENT_INTERACTION_MODE_EXPERT,
]

const loadSettings = () => {
    try {
        const artistSettingsItem = localStorage.getItem(ARTIST_SETTINGS)
        if (!artistSettingsItem) {
            return {}
        }
        const parsed = JSON.parse(artistSettingsItem)
        return parsed && typeof parsed === 'object' ? parsed : {}
    } catch {
        // Backward-compatibility: tolerate corrupted storage without breaking the app.
        return {}
    }
}

export const loadArtistSettings = (artistId) => {
    const allArtistSettings = loadSettings()
    return allArtistSettings && allArtistSettings[artistId] || {}
}

const saveAllArtistSettings = (artistSettings) => localStorage.setItem(ARTIST_SETTINGS, JSON.stringify(artistSettings || {}))

const saveArtistSetting = (artistId, fieldValues) => {
    const allArtistSettings = loadSettings()
    const artistSettings = allArtistSettings[artistId] || {}
    allArtistSettings[artistId] = {
        ...artistSettings
        , ...fieldValues
    }
    saveAllArtistSettings(allArtistSettings)
}

export const saveArtistEventFilter = (artistId, eventFilter) => {
    saveArtistSetting(artistId, {eventFilter})
}

export const saveArtistInfoSettings = ({artistId, disabledInfoChanges}) => {
    const allArtistSettings = loadSettings()
    const artistSettings = allArtistSettings[artistId] || {}
    const artistInfoSettings = artistSettings?.disabledInfo || {}
    const disabledInfo = {
            ...artistInfoSettings
            , ...disabledInfoChanges
        }
    saveArtistSetting(artistId, {disabledInfo})
}

export const saveArtistResourceFilter = (artistId, resourceFilter) => {
    saveArtistSetting(artistId, {resourceFilter})
}

export const saveArtistThemeSetting = (artistId, themeSettings) => {
    saveArtistSetting(artistId, {themeSettings})
}

export const saveArtistTimezoneInfoSeen = (artistId) => {
    saveArtistSetting(artistId, {timezoneInfoSeen: true})
}

export const saveArtistLocale = (artistId, locale) => {
    saveArtistSetting(artistId, {locale})
}

export const loadArtistShiftPlanningUi = (artistId) => {
    const settings = loadArtistSettings(artistId)
    return settings?.shiftPlanning
}

export const saveArtistShiftPlanningUi = (artistId, shiftPlanning) => {
    saveArtistSetting(artistId, { shiftPlanning })
}

export const loadArtistLocale = (artistId) => {
    const settings = loadArtistSettings(artistId)
    return settings?.locale
}

export const normalizeAppointmentInteractionMode = (interactionMode) => {
    return APPOINTMENT_INTERACTION_MODES.includes(interactionMode)
        ? interactionMode
        : APPOINTMENT_INTERACTION_MODE_NOVICE
}

export const loadArtistAppointmentInteractionMode = (artistId) => {
    if (!artistId) {
        return APPOINTMENT_INTERACTION_MODE_NOVICE
    }
    const settings = loadArtistSettings(artistId)
    return normalizeAppointmentInteractionMode(settings?.appointmentInteractionMode)
}

export const saveArtistAppointmentInteractionMode = (artistId, interactionMode) => {
    if (!artistId) {
        return
    }
    saveArtistSetting(artistId, {
        appointmentInteractionMode: normalizeAppointmentInteractionMode(interactionMode),
    })
}

export const loadArtistRecentUsage = (artistId) => {
    const settings = loadArtistSettings(artistId)
    return coerceRecentArtistsMap(settings?.recentArtists)
}

export const saveArtistRecentUsage = (artistId, recentArtists) => {
    const sanitized = coerceRecentArtistsMap(recentArtists)
    const capped = capRecentArtistsMap({ recentArtists: sanitized, maxEntries: MAX_RECENT_ARTISTS_PERSISTED })
    saveArtistSetting(artistId, {recentArtists: capped})
}

export const trackArtistRecentUsage = ({ownerArtistId, usedArtistId}) => {
    if (!ownerArtistId || !usedArtistId) {
        return
    }
    const now = Date.now()
    const recentArtists = loadArtistRecentUsage(ownerArtistId)
    const updated = upsertRecentArtistsUsage({
        recentArtists,
        usedArtistId,
        now,
    })
    const capped = capRecentArtistsMap({ recentArtists: updated, maxEntries: MAX_RECENT_ARTISTS_PERSISTED, now })
    saveArtistSetting(ownerArtistId, { recentArtists: capped })
}

/**
 * Cleans up recent artist usage by removing artists that are no longer accessible
 * @param {number} ownerArtistId - ID of the logged-in artist (owner)
 * @param {number[]} accessibleArtistIds - Array of artist IDs that the owner can still access
 */
export const cleanupArtistRecentUsage = (ownerArtistId, accessibleArtistIds) => {
    if (!ownerArtistId) {
        return
    }

    const accessibleArtistIdSet = new Set(accessibleArtistIds?.map(id => String(id)) || [])
    const recentArtists = loadArtistRecentUsage(ownerArtistId)
    
    if (!recentArtists || Object.keys(recentArtists).length === 0) {
        return
    }

    // Filter out artists that are no longer accessible
    const cleaned = Object.entries(recentArtists).reduce((acc, [artistId, usage]) => {
        if (accessibleArtistIdSet.has(artistId)) {
            acc[artistId] = usage
        }
        return acc
    }, {})

    // Only save if something was removed
    if (Object.keys(cleaned).length !== Object.keys(recentArtists).length) {
        saveArtistRecentUsage(ownerArtistId, cleaned)
    }
}
